import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { reportError } from '../../lib/errorEvents';
import { parseProjectNumberDraft, projectNumberRequestError } from '../../lib/projectNumbers';
import { useIsMobile } from '../../lib/useIsMobile';
import { ARCHIVE_DRIVES, PHASE_LABELS, type ClientWithCounts, type ProjectPhase } from '../../../shared/types';
import { DateField, formatProjectNumber, Modal, Seg, SelectField } from '../../components/primitives';
import { ACTIVE_PHASES } from './constants';

// ── New project modal (09-new-project) — also opened from client detail ──

export function NewProjectModal({
  onClose,
  onCreated,
  defaultClientId,
  prefill,
}: {
  onClose: () => void;
  onCreated: () => void;
  defaultClientId?: string;
  prefill?: { name: string; shoot_date: string; shoot_end_date: string | null; client_text: string | null; note?: string | null };
}) {
  const isMobile = useIsMobile();
  const [clients, setClients] = useState<ClientWithCounts[]>([]);
  const [templates, setTemplates] = useState<{ id: string; name: string; items: unknown[] }[]>([]);
  // Inline client creation (Phase 1.7): 'existing' picks from the register,
  // 'new' creates the client on submit (mirrors the lead-convert modal).
  const [clientMode, setClientMode] = useState<'existing' | 'new'>(!defaultClientId && prefill?.client_text ? 'new' : 'existing');
  const clientTouched = useRef(false);
  const [newClientName, setNewClientName] = useState(prefill?.client_text ?? '');
  const [form, setForm] = useState({
    name: prefill?.name ?? '',
    client_id: (defaultClientId ?? null) as string | null,
    project_number: '',
    project_number_source: 'pending' as 'pending' | 'suggested' | 'edited',
    phase: 'lead' as ProjectPhase,
    shoot_date: prefill?.shoot_date ?? null as string | null,
    shoot_end_date: prefill?.shoot_end_date ?? null as string | null,
    edit_date: null as string | null,
    due_date: null as string | null,
    archive_drive: '',
    dashboard: 'later' as 'later' | 'client',
    template_id: null as string | null,
    notes: prefill?.note ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ clients: ClientWithCounts[] }>('/api/admin/clients')
      .then((d) => {
        setClients(d.clients);
        // Free text stays editable as a new client unless it exactly matches
        // a registered client, in which case re-use the existing relationship.
        const match = !clientTouched.current && !defaultClientId && prefill?.client_text && d.clients.find(
          (client) => client.name.trim().toLowerCase() === prefill.client_text!.trim().toLowerCase(),
        );
        if (match) {
          setClientMode('existing');
          setForm((f) => ({ ...f, client_id: match.id }));
        }
      })
      .catch(() => {});
    api<{ templates: { id: string; name: string; items: unknown[] }[] }>('/api/admin/task-templates')
      .then((d) => setTemplates(d.templates))
      .catch(() => {});
    api<{ next: number | null }>('/api/admin/projects/next-number')
      .then((d) => {
        setForm((current) => (
          current.project_number_source === 'pending'
            ? {
                ...current,
                project_number: d.next === null ? '' : String(d.next),
                project_number_source: 'suggested',
              }
            : current
        ));
      })
      .catch(() => {});
  }, []);

  const selectedClient = clients.find((c) => c.id === form.client_id);

  const clientReady = clientMode === 'existing' ? !!form.client_id : !!newClientName.trim();

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!form.name.trim() || !clientReady || busy) return;
    const projectNumber = parseProjectNumberDraft(form.project_number);
    if (projectNumber.error) {
      setError(projectNumber.error);
      return;
    }
    clientTouched.current = true;
    setBusy(true);
    setError(null);
    try {
      // New-client path: create the client first, then link the project to it.
      // If the project create then 409s on its number, the client survives in
      // form state — retrying reuses it instead of creating a second one.
      let clientId = form.client_id;
      if (clientMode === 'new' && !clientId) {
        const created = await api<{ client: { id: string } }>('/api/admin/clients', {
          method: 'POST',
          json: { name: newClientName.trim() },
        });
        clientId = created.client.id;
        setForm((f) => ({ ...f, client_id: clientId }));
      }
      const res = await api<{ project: { id: string } }>('/api/admin/projects', {
        method: 'POST',
        json: {
          name: form.name.trim(),
          client_id: clientId,
          ...(form.project_number_source === 'pending'
            ? {}
            : { project_number: projectNumber.value }),
          phase: form.phase,
          shoot_date: form.shoot_date,
          shoot_end_date: form.shoot_end_date,
          edit_date: form.edit_date,
          due_date: form.due_date,
          archive_drive_1: form.archive_drive || null,
          dashboard_slug:
            form.dashboard === 'client' ? (selectedClient?.dashboard_slug ?? null) : null,
          summary: form.notes || null,
        },
      });
      if (form.template_id) {
        await api(`/api/admin/task-templates/${form.template_id}/apply`, {
          method: 'POST',
          json: { project_id: res.project.id },
        }).catch((error) => reportError(error, 'Project created, but its task template could not be applied.'));
      }
      onCreated();
      onClose();
    } catch (err) {
      setError(projectNumberRequestError(err, 'Failed to create project.'));
      setBusy(false);
    }
  };

  const dashboardItems = [
    { value: 'later', label: 'Create later…' },
    ...(selectedClient?.dashboard_slug
      ? [{ value: 'client', label: `Link /${selectedClient.dashboard_slug}` }]
      : []),
  ] as const;

  return (
    <Modal
      title="New project"
      mobile="full"
      context={isMobile ? `Lands on the ${PHASE_LABELS[form.phase]} shelf` : "PROJECTS"}
      onClose={onClose}
      hint={isMobile ? undefined : "Lands on the Onboarding shelf"}
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={() => submit()}
            disabled={!form.name.trim() || !clientReady || busy}
          >
            Create project →
          </button>
        </>
      }
    >
      <form className={isMobile ? "form-grid project-list-new" : "form-grid"} onSubmit={submit}>
        <div className="f-field span-2">
          <span className="f-label">
            Project name <span className="req">*</span>
          </span>
          <input
            className="f-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            autoFocus
          />
        </div>
        <div className="f-field">
          <span className="f-label">
            Client <span className="req">*</span>
            <Seg
              options={[
                { value: 'existing', label: 'Existing' },
                { value: 'new', label: 'New' },
              ]}
              value={clientMode}
              onChange={(v) => { clientTouched.current = true; setClientMode(v as 'existing' | 'new'); }}
            />
          </span>
          {clientMode === 'existing' ? (
            <SelectField
              label={isMobile ? "Client" : undefined}
              items={clients.map((c) => ({ value: c.id, label: c.name }))}
              value={form.client_id}
              onChange={(client_id) => { clientTouched.current = true; setForm((f) => ({ ...f, client_id })); }}
              placeholder="Pick a client…"
              searchable
            />
          ) : (
            <input
              className="f-input"
              value={newClientName}
              onChange={(e) => { clientTouched.current = true; setNewClientName(e.target.value); }}
              placeholder="New client name…"
            />
          )}
        </div>
        <div className="f-field">
          <span className="f-label">Phase</span>
          <SelectField
            label={isMobile ? "Phase" : undefined}
              items={ACTIVE_PHASES.map((p) => ({ value: p, label: PHASE_LABELS[p] }))}
            value={form.phase}
            onChange={(phase) => setForm((f) => ({ ...f, phase }))}
          />
        </div>
        <div className="f-field">
          <span className="f-label">Project number</span>
          <input
            className="f-input mono"
            type="number"
            min={1}
            step={1}
            value={form.project_number}
            onChange={(e) => setForm((f) => ({
              ...f,
              project_number: e.target.value,
              project_number_source: 'edited',
            }))}
            placeholder="Unnumbered"
          />
          {parseProjectNumberDraft(form.project_number).value !== null && (
            <span className="f-help">
              Displays as {formatProjectNumber(parseProjectNumberDraft(form.project_number).value)}
            </span>
          )}
        </div>
        <div className="f-field">
          <span className="f-label">Shoot date</span>
          <DateField
            keyboardNavigation={!!prefill} label={prefill || isMobile ? "Shoot date" : undefined} value={form.shoot_date}
            onChange={(shoot_date) => setForm((f) => ({ ...f, shoot_date }))}
          />
        </div>
        {prefill && <div className="f-field">
          <span className="f-label">Shoot end date <span className="opt">optional</span></span>
          <DateField keyboardNavigation label="Shoot end date" value={form.shoot_end_date}
            onChange={(shoot_end_date) => setForm((f) => ({ ...f, shoot_end_date }))} placeholder="Same day" />
        </div>}
        <div className="f-field">
          <span className="f-label">Edit date</span>
          <DateField
            label={isMobile ? "Edit date" : undefined} value={form.edit_date}
            onChange={(edit_date) => setForm((f) => ({ ...f, edit_date }))}
          />
        </div>
        <div className="f-field">
          <span className="f-label">Due date</span>
          <DateField label={isMobile ? "Due date" : undefined} value={form.due_date} onChange={(due_date) => setForm((f) => ({ ...f, due_date }))} />
        </div>
        <div className="f-field">
          <span className="f-label">Archive drive</span>
          <input
            className="f-input mono"
            value={form.archive_drive}
            onChange={(e) => setForm((f) => ({ ...f, archive_drive: e.target.value }))}
            placeholder="ARC-2026-…"
            list="archive-drives"
          />
          <datalist id="archive-drives">
            {ARCHIVE_DRIVES.map((d) => (
              <option key={d} value={d} />
            ))}
          </datalist>
        </div>
        <div className="f-field">
          <span className="f-label">Client dashboard</span>
          <SelectField
            label={isMobile ? "Client dashboard" : undefined}
              items={dashboardItems as never}
            value={form.dashboard}
            onChange={(dashboard) => setForm((f) => ({ ...f, dashboard: dashboard as 'later' | 'client' }))}
          />
        </div>
        {(isMobile || templates.length > 0) && (
          <div className="f-field">
            <span className="f-label">Tasks</span>
            <SelectField
              label={isMobile ? "Tasks" : undefined}
              items={[
                { value: '', label: 'No template' },
                ...templates.map((t) => ({ value: t.id, label: `${t.name} · ${t.items.length}` })),
              ]}
              value={form.template_id ?? ''}
              onChange={(template_id) => setForm((f) => ({ ...f, template_id: template_id || null }))}
              placeholder="Apply template…"
            />
          </div>
        )}
        <div className="f-field span-2">
          <span className="f-label">Notes</span>
          <textarea
            className="f-textarea"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Scope, deliverables, key contacts…"
          />
        </div>
        {error && <p className="error-text span-2">{error}</p>}
      </form>
    </Modal>
  );
}
